import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { showAlertModal } from "../lib/custom_confirm";
import { ROW_ACTION_DANGER_CLASS } from "../lib/row_action";

// Field picker for the Export Lead Profile modal form (new + edit): ticking an
// available field appends it to the selected list, the list is drag-sortable,
// and submit serialises the selection as export_profile_fields_attributes.
//
// The form loads into a Turbo Frame inside a React modal, so Stimulus connects
// it on insertion.
//
// Picker tools (#17081): a search box and a lead-type filter hide available
// rows, Select All ticks every VISIBLE field, Clear empties the selection, and
// the header preview shows the row the export will start with.
//
// Connects to data-controller="export-profile-form" on the <form>.
export default class extends Controller {
  static targets = [
    "selectedFields", "search", "leadTypeFilter", "group", "noMatches", "headerPreview",
    "name", "nameError", "nameHint", "emptyHint", "dragHint",
  ];
  static values = { removeLabel: { type: String, default: "Remove field" } };

  connect() {
    this.syncCheckboxesToSelection();
    this.updatePreview();
    this.sortable = new Sortable(this.selectedFieldsTarget, {
      animation: 150,
      ghostClass: "sortable-ghost",
      onEnd: () => this.renumber(),
    });
  }

  disconnect() {
    this.sortable?.destroy();
    this.sortable = null;
  }

  // change->export-profile-form#toggleField on each available-field checkbox.
  toggleField(event) {
    const checkbox = event.currentTarget;
    if (checkbox.checked) {
      this.addField(checkbox.dataset.fieldKey, checkbox.dataset.displayName);
    } else {
      this.removeFieldByKey(checkbox.dataset.fieldKey);
    }
  }

  // click->export-profile-form#removeField on a selected field's remove button.
  removeField(event) {
    const field = event.currentTarget.closest(".selected-field");
    if (!field) return;

    this.removeFieldByKey(field.dataset.fieldKey);
    const checkbox = this.checkboxFor(field.dataset.fieldKey);
    if (checkbox) checkbox.checked = false;
  }

  // input->export-profile-form#filter (search) / change-> (lead-type filter).
  filter() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : "";
    const leadType = this.hasLeadTypeFilterTarget ? this.leadTypeFilterTarget.value : "";
    let visible = 0;

    this.optionElements().forEach((option) => {
      const matchesQuery = !query || (option.dataset.search || "").includes(query);
      const types = option.dataset.leadTypes ? option.dataset.leadTypes.split("|") : null;
      const matchesType = !leadType || !types || types.includes(leadType);
      const show = matchesQuery && matchesType;
      option.classList.toggle("d-none", !show);
      if (show) visible += 1;
    });

    this.groupTargets.forEach((group) => {
      const anyVisible = group.querySelector(".field-option:not(.d-none)");
      group.classList.toggle("d-none", !anyVisible);
    });
    if (this.hasNoMatchesTarget) this.noMatchesTarget.classList.toggle("d-none", visible > 0);
  }

  // click->export-profile-form#selectAll: every visible, unticked field, in list order.
  selectAll() {
    this.optionElements()
      .filter((option) => !option.classList.contains("d-none"))
      .forEach((option) => {
        const checkbox = option.querySelector(".field-checkbox");
        if (!checkbox || checkbox.checked) return;
        checkbox.checked = true;
        this.addField(checkbox.dataset.fieldKey, checkbox.dataset.displayName);
      });
  }

  // click->export-profile-form#selectNone: empties the selection.
  selectNone() {
    this.selectedFieldElements().forEach((field) => field.remove());
    this.element.querySelectorAll(".field-checkbox").forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.renumber();
  }

  // input->export-profile-form#clearNameError on the Name box.
  clearNameError() {
    if (this.hasNameTarget && this.nameTarget.value !== "") this.setNameInvalid(false);
  }

  // submit->export-profile-form#submit on the form. The form is novalidate, so a
  // blank Name is caught here — red outline and the message under the box —
  // instead of the browser's "Please fill out this field" bubble (#17081).
  // Only a truly empty Name is caught here (what `required` blocked); a
  // whitespace-only one still goes to the server, whose presence validation
  // refuses it with the same red outline.
  submit(event) {
    if (this.hasNameTarget && this.nameTarget.value === "") {
      event.preventDefault();
      this.setNameInvalid(true);
      this.nameTarget.focus();
      return;
    }

    const selected = this.selectedFieldElements();
    if (selected.length === 0) {
      event.preventDefault();
      showAlertModal("Please select at least one field for your export lead profile.", {
        variant: "warning",
        title: "Notice",
      });
      return;
    }

    this.element
      .querySelectorAll('input[type="hidden"][name*="export_profile_fields_attributes"]')
      .forEach((input) => input.remove());

    selected.forEach((field, index) => {
      this.appendHidden(index, "field_key", field.dataset.fieldKey);
      this.appendHidden(index, "display_name", field.dataset.displayName);
      this.appendHidden(index, "position", String(index + 1));
    });
  }

  addField(fieldKey, displayName) {
    if (this.selectedFieldsTarget.querySelector(`[data-field-key="${CSS.escape(fieldKey)}"]`)) return;

    const field = document.createElement("div");
    field.className = "selected-field draggable-field mb-2 p-2 border rounded";
    field.dataset.fieldKey = fieldKey;
    field.dataset.displayName = displayName;

    const row = document.createElement("div");
    row.className = "d-flex justify-content-between align-items-center";
    const label = document.createElement("span");
    label.textContent = displayName;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = `${ROW_ACTION_DANGER_CLASS} remove-field`;
    remove.dataset.glTooltip = this.removeLabelValue;
    remove.setAttribute("aria-label", this.removeLabelValue);
    remove.dataset.action = "click->export-profile-form#removeField";
    const icon = document.createElement("i");
    icon.className = "ph ph-x";
    icon.setAttribute("aria-hidden", "true");
    remove.appendChild(icon);

    row.append(label, remove);
    field.appendChild(row);
    this.selectedFieldsTarget.appendChild(field);
    this.renumber();
  }

  removeFieldByKey(fieldKey) {
    const field = this.selectedFieldsTarget.querySelector(`[data-field-key="${CSS.escape(fieldKey)}"]`);
    if (!field) return;

    field.remove();
    this.renumber();
  }

  // Editing an existing profile: its saved fields render pre-selected, so tick
  // their checkboxes on connect.
  syncCheckboxesToSelection() {
    this.selectedFieldElements().forEach((field) => {
      const checkbox = this.checkboxFor(field.dataset.fieldKey);
      if (checkbox) checkbox.checked = true;
    });
  }

  renumber() {
    this.selectedFieldElements().forEach((field, index) => {
      field.dataset.position = String(index + 1);
    });
    this.updatePreview();
  }

  setNameInvalid(invalid) {
    this.nameTarget.classList.toggle("is-invalid", invalid);
    if (invalid) this.nameTarget.setAttribute("aria-invalid", "true");
    else this.nameTarget.removeAttribute("aria-invalid");
    if (this.hasNameErrorTarget) {
      this.nameErrorTarget.textContent = invalid ? this.nameErrorTarget.dataset.message || "" : "";
      this.nameErrorTarget.classList.toggle("d-block", invalid);
    }
    // The grey hint gives way to the red error, and returns once it clears.
    if (this.hasNameHintTarget) this.nameHintTarget.classList.toggle("d-none", invalid);
  }

  // Selected Fields hints: "Check fields on the left" while empty, "Drag to
  // reorder" only once there are two fields to reorder.
  updateHints(count) {
    if (this.hasEmptyHintTarget) this.emptyHintTarget.classList.toggle("d-none", count > 0);
    if (this.hasDragHintTarget) this.dragHintTarget.classList.toggle("d-none", count < 2);
  }

  updatePreview() {
    this.updateHints(this.selectedFieldElements().length);
    if (!this.hasHeaderPreviewTarget) return;

    const names = this.selectedFieldElements().map((field) => field.dataset.displayName);
    this.headerPreviewTarget.textContent = names.length ? names.join(", ") : "No Fields Selected Yet.";
  }

  optionElements() {
    return Array.from(this.element.querySelectorAll(".field-option"));
  }

  selectedFieldElements() {
    return Array.from(this.selectedFieldsTarget.querySelectorAll(".selected-field"));
  }

  checkboxFor(fieldKey) {
    return this.element.querySelector(`.field-checkbox[data-field-key="${CSS.escape(fieldKey)}"]`);
  }

  appendHidden(index, attribute, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `export_profile[export_profile_fields_attributes][${index}][${attribute}]`;
    input.value = value;
    this.element.appendChild(input);
  }
}
